/**
 * SUPPORT-ONLY. Search controls for the OCPP timelog tool.
 *
 * The Charger ID + date-range fields are the canonical query (and are filled in
 * automatically when a receipt/transaction/session lookup resolves a device +
 * window). The three "resolve from" inputs are shortcuts that populate them.
 */
import { useTranslation } from 'react-i18next';
import { Button, ButtonType, Divider, FormInput } from '../../../_ui';
import { InputType } from '../../../_ui/Input.component';
import { TimeRangePicker, type TimeSpec } from './TimeRangePicker.component';

interface LookupPanelProps {
  /** Re-mounts the uncontrolled text inputs so resolved values appear in them. */
  fieldKey: number;
  busy: boolean;
  chargerId: string;
  onChargerIdChange: (value: string) => void;
  from: TimeSpec;
  to: TimeSpec;
  onTimeRangeChange: (from: TimeSpec, to: TimeSpec) => void;
  onLoadCharger: () => void;
  /** Minutes of padding to prepend before the session window (receipt/txn/session lookups). */
  paddingBefore: string;
  onPaddingBeforeChange: (value: string) => void;
  /** Minutes of padding to append after the session window (receipt/txn/session lookups). */
  paddingAfter: string;
  onPaddingAfterChange: (value: string) => void;
  receiptId: string;
  onReceiptIdChange: (value: string) => void;
  onLoadReceipt: () => void;
  txnId: string;
  onTxnIdChange: (value: string) => void;
  onLoadTransaction: () => void;
  sessionId: string;
  onSessionIdChange: (value: string) => void;
  onLoadSession: () => void;
}

export const LookupPanel = ({
  fieldKey,
  busy,
  chargerId,
  onChargerIdChange,
  from,
  to,
  onTimeRangeChange,
  onLoadCharger,
  paddingBefore,
  onPaddingBeforeChange,
  paddingAfter,
  onPaddingAfterChange,
  receiptId,
  onReceiptIdChange,
  onLoadReceipt,
  txnId,
  onTxnIdChange,
  onLoadTransaction,
  sessionId,
  onSessionIdChange,
  onLoadSession,
}: LookupPanelProps) => {
  const { t } = useTranslation();

  return (
    <div className='flex flex-col gap-6'>
      {/* Canonical query: charger + window */}
      <div className='flex flex-col gap-3'>
        <TimeRangePicker from={from} to={to} onChange={onTimeRangeChange} />
        <div className='flex items-end gap-3'>
          <div className='w-96'>
            <FormInput
              key={`charger-${fieldKey}`}
              label={t('ocpp_timelog.charger_id_label')}
              placeholder={t('ocpp_timelog.charger_id_placeholder')}
              defaultValue={chargerId}
              onChange={(e) => onChargerIdChange(e.target.value)}
              onEnterKeyPress={(e: React.KeyboardEvent) => {
                if (e.key === 'Enter') onLoadCharger();
              }}
              dataTestId='ocpp-timelog-charger-id'
              width='100%'
            />
          </div>
          <Button
            label={t('ocpp_timelog.load_button')}
            type={ButtonType.PRIMARY}
            onClick={onLoadCharger}
            disabled={busy || !chargerId.trim()}
          />
        </div>
      </div>

      <Divider />

      {/* Shortcuts that resolve a device + window into the fields above */}
      <div className='flex flex-col gap-2'>
        <span className='text-sm font-medium text-grey5'>
          {t('ocpp_timelog.resolve_from_label')}
        </span>
        <div className='flex items-end gap-3'>
          <div className='w-36'>
            <FormInput
              label={t('ocpp_timelog.padding_before_label')}
              defaultValue={paddingBefore}
              onChange={(e) => onPaddingBeforeChange(e.target.value)}
              inputType={InputType.POSITIVE_NUMBER}
              dataTestId='ocpp-timelog-padding-before'
              width='100%'
            />
          </div>
          <div className='w-36'>
            <FormInput
              label={t('ocpp_timelog.padding_after_label')}
              defaultValue={paddingAfter}
              onChange={(e) => onPaddingAfterChange(e.target.value)}
              inputType={InputType.POSITIVE_NUMBER}
              dataTestId='ocpp-timelog-padding-after'
              width='100%'
            />
          </div>
        </div>
        <div className='flex flex-wrap items-end gap-4'>
          <div className='flex items-end gap-2'>
            <div className='w-96'>
              <FormInput
                key={`receipt-${fieldKey}`}
                label={t('ocpp_timelog.receipt_id_label')}
                placeholder={t('ocpp_timelog.receipt_id_placeholder')}
                defaultValue={receiptId}
                onChange={(e) => onReceiptIdChange(e.target.value)}
                onEnterKeyPress={(e: React.KeyboardEvent) => {
                  if (e.key === 'Enter') onLoadReceipt();
                }}
                dataTestId='ocpp-timelog-receipt-id'
                width='100%'
              />
            </div>
            <Button
              label={t('ocpp_timelog.resolve_button')}
              type={ButtonType.SECONDARY}
              onClick={onLoadReceipt}
              disabled={busy || !receiptId.trim()}
            />
          </div>

          <div className='flex items-end gap-2'>
            <div className='w-96'>
              <FormInput
                key={`txn-${fieldKey}`}
                label={t('ocpp_timelog.transaction_id_label')}
                placeholder={t('ocpp_timelog.transaction_id_placeholder')}
                defaultValue={txnId}
                onChange={(e) => onTxnIdChange(e.target.value)}
                onEnterKeyPress={(e: React.KeyboardEvent) => {
                  if (e.key === 'Enter') onLoadTransaction();
                }}
                dataTestId='ocpp-timelog-transaction-id'
                width='100%'
              />
            </div>
            <Button
              label={t('ocpp_timelog.resolve_button')}
              type={ButtonType.SECONDARY}
              onClick={onLoadTransaction}
              disabled={busy || !txnId.trim()}
            />
          </div>

          <div className='flex items-end gap-2'>
            <div className='w-96'>
              <FormInput
                key={`session-${fieldKey}`}
                label={t('ocpp_timelog.session_id_label')}
                placeholder={t('ocpp_timelog.session_id_placeholder')}
                defaultValue={sessionId}
                onChange={(e) => onSessionIdChange(e.target.value)}
                onEnterKeyPress={(e: React.KeyboardEvent) => {
                  if (e.key === 'Enter') onLoadSession();
                }}
                dataTestId='ocpp-timelog-session-id'
                width='100%'
              />
            </div>
            <Button
              label={t('ocpp_timelog.resolve_button')}
              type={ButtonType.SECONDARY}
              onClick={onLoadSession}
              disabled={busy || !sessionId.trim()}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
